import React, { useState } from "react";
import divorceCostBoxStyle from "./divorceCostBoxStyle";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import CostOverviewContainer from "@/src/components/Serviceworld/Divorce/CostOverviewContainer";
import { useLocation, useNavigate } from "react-router";
import { calculateFees, calculateValueInDispute } from "@/src/services/costCalculation";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import DivorceCostCalculatorForm from "./DivorceCostCalculatorForm";
import { Divider, Typography } from "@mui/material";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import { DivorceFees, ValueInDispute } from "@/src/types/CostCalculatorDivorce";
import { gaEvent } from "@/src/services/googleAnalyticsService";
import AlertLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertLegalbird";
import AlertContentLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertContentLegalbird";

const DivorceCostCalculator = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [valueInDispute, setValueInDispute] = useState<ValueInDispute | null>(null);
  const [fees, setFees] = useState<DivorceFees | null>(null);

  const initialValues = {
    netIncomePetitioner: "",
    netIncomePartner: "",
    numberPensions: "",
    numberPensionsPartner: "",
    pensionsExcluded: false,
  };

  const handleSubmit = ({ values }: { values: FormDataTypes["values"] }) => {
    const vid = calculateValueInDispute(values);
    const calculatedFees = calculateFees(vid.total);
    setValueInDispute(vid);
    setFees(calculatedFees);
  };

  const toRecalc = () => {
    gaEvent({
      category: "Cost Calculator",
      action: "Re-calculate",
    });
    setValueInDispute(null);
    setFees(null);
  };

  const costDetailModalClose = () => {
    navigate(-1);
  };

  if (valueInDispute && fees) {
    return (
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid size={12}>
          <Grid
            container
            sx={{
              justifyContent: "space-between",
            }}
          >
            <Grid sx={[divorceCostBoxStyle.costEstimateLabel, divorceCostBoxStyle.primary]}>Kostenschätzung</Grid>
            <Grid sx={[divorceCostBoxStyle.costEstimateValue, divorceCostBoxStyle.primary]}>
              {fees.total.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}*
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Button
            sx={divorceCostBoxStyle.costButton}
            variant="contained"
            onClick={() => navigate(location.pathname, { state: { costLayerModalOpen: true } })}
            fullWidth
          >
            Kosten im Detail
          </Button>
        </Grid>
        <Grid size={12} sx={{ marginBottom: "1rem" }}>
          <Button onClick={toRecalc} sx={divorceCostBoxStyle.costButton} variant="contained" fullWidth>
            Neu berechnen
          </Button>
        </Grid>
        <Grid size={12} sx={{ marginBottom: "1rem" }}>
          <Typography sx={divorceCostBoxStyle.costDisclaimerText}>
            *Dies ist eine Vorschusskosten-Rechnung. Die letztendlich anfallenden Kosten werden vom Gericht festgesetzt.
          </Typography>
          <Divider />
        </Grid>
        <Grid size={12}>
          <AlertLegalbird severity={"success"} variant={"hint"}>
            <AlertContentLegalbird
              title={"Fragen zur Finanzierung?"}
              subtitle={"Melden Sie sich dazu gerne direkt bei uns und wir finden gemeinsam das für Sie passende Modell!"}
            />
          </AlertLegalbird>
        </Grid>
        <DialogModal open={!!(location.state && location.state.costLayerModalOpen)} onClose={costDetailModalClose} title={"Kostenschätzung"}>
          <CostOverviewContainer fees={fees} valueInDispute={valueInDispute} />
        </DialogModal>
      </Grid>
    );
  }

  return (
    <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
      <DivorceCostCalculatorForm />
    </FormProvider>
  );
};

export default DivorceCostCalculator;
